import React from "react";
import { LegalPageShell } from "./Legal";

const Privacy = () => (
  <LegalPageShell id="privacy">
    <main id="main-content">
      <svg
        width="0"
        height="0"
        style={{ position: "absolute" }}
        aria-hidden="true"
        focusable="false"
      >
        <defs>
          <g id="blob-1" transform="translate(-100,-100)">
            <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
            <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
          </g>
          <g id="blob-2" transform="translate(100,-100) scale(-1,1)">
            <path d="M192.43,117.14c11.53-46.12-30-39.2-53-53s-17.5-33.22-57.65-36.89S14.78,47.91,8,82.55s.25,82.91,53,94.55S180.9,163.26,192.43,117.14Z" />
            <path d="M149.36,21.44c-6.46,4.67-18.7,21.13-8.53,30.51s27.73,6.23,35.8-8.82c7.22-13.44-2.11-20.87-8.75-23.83S153.07,18.82,149.36,21.44Z" />
          </g>
        </defs>
      </svg>
      <header className="hero">
        <div className="hero-pattern" aria-hidden="true">
          <svg
            width="100%"
            height="100%"
            preserveAspectRatio="xMidYMid slice"
            xmlns="http://www.w3.org/2000/svg"
          >
            <svg x="6%" y="22%" overflow="visible">
              <use
                href="#blob-1"
                fill="#FFA5BC"
                transform="scale(1.5)"
                opacity="0.16"
              />
            </svg>
            <svg x="84%" y="76%" overflow="visible">
              <use
                href="#blob-2"
                fill="#B798F2"
                transform="scale(1.8)"
                opacity="0.14"
              />
            </svg>
            <svg x="50%" y="-15%" overflow="visible">
              <use
                href="#blob-1"
                fill="#E8EBFF"
                transform="scale(1.4)"
                opacity="0.55"
              />
            </svg>
            <svg x="68%" y="58%" overflow="visible">
              <use
                href="#blob-2"
                fill="#F3EC7A"
                transform="scale(0.85)"
                opacity="0.30"
              />
            </svg>
          </svg>
        </div>
        <div className="container">
          <span className="eyebrow">Privacy Policy</span>
          <h1 className="hero-title">
            Your data, <em>in plain English</em>.
          </h1>
          <p className="hero-sub">
            <strong>
              We collect what we need, never sell it, and never use it to train
              AI.
            </strong>{" "}
            Below is the legal version, with a friendly translation alongside.{" "}
          </p>
          <div className="hero-meta" aria-label="Policy metadata">
            <span>
              <strong>Read time:</strong> about 8 minutes
            </span>
          </div>
        </div>
      </header>
      <section className="tldr-wrap" aria-labelledby="tldr-title">
        <div className="narrow">
          <div className="tldr">
            <div className="label">If you only read one thing</div>
            <h2 id="tldr-title">
              The whole policy, <em>in six bullets</em>.
            </h2>
            <ul>
              <li>
                <strong>We collect</strong> the things we need to run your
                account, mark your work, take payment, and improve the site.
                That is all.
              </li>
              <li>
                <strong>We never sell your data.</strong> Partly because we
                wouldn't, and partly because nobody is buying.
              </li>
              <li>
                <strong>We never use your data to train AI.</strong> Your
                answers, your role, your goals, your location: yours, not
                training fuel.
              </li>
              <li>
                <strong>We share data only</strong> with the suppliers we need
                to run the platform (payment, hosting, email), under written
                contracts.
              </li>
              <li>
                <strong>You have rights</strong> under UK GDPR. We will not make
                you fight for any of them.
              </li>
              <li>
                <strong>If something is unclear,</strong> email{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
                . We reply within 24 hours, often sooner.
              </li>
            </ul>
          </div>
        </div>
      </section>
      <nav className="toc" aria-label="Jump to a section">
        <div className="container toc-inner">
          <span className="toc-label">Jump to:</span>
          <div className="toc-pills">
            <a href="#who-we-are" className="toc-pill">
              Who we are
            </a>
            <a href="#what-we-collect" className="toc-pill">
              What we collect
            </a>
            <a href="#lawful-bases" className="toc-pill">
              Lawful bases
            </a>
            <a href="#ai-privacy" className="toc-pill">
              AI & your data
            </a>
            <a href="#cookies" className="toc-pill">
              Cookies
            </a>
            <a href="#sharing" className="toc-pill">
              Sharing
            </a>
            <a href="#retention" className="toc-pill">
              Retention
            </a>
            <a href="#your-rights" className="toc-pill">
              Your rights
            </a>
            <a href="#children" className="toc-pill">
              Children
            </a>
            <a href="#security" className="toc-pill">
              Security
            </a>
            <a href="#changes" className="toc-pill">
              Changes
            </a>
            <a href="#contact" className="toc-pill">
              Contact
            </a>
          </div>
        </div>
      </nav>
      <section id="who-we-are" aria-labelledby="who-we-are-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">01</span>
            <h2 className="section-title" id="who-we-are-title">
              Who <em>we are</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              We are 42courses. We run the website. We decide how your data is
              handled. The buck stops with us.{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              This privacy policy is issued by{" "}
              <strong>42COURSES.COM LIMITED</strong> (referred to in this policy
              as <em>42courses</em>, <em>we</em>, <em>us</em>, and <em>our</em>
              ). We are a company registered in England and Wales and the
              operator of{" "}
              <a href="https://www.42courses.com">www.42courses.com</a>.
            </p>
            <p>
              For the purposes of UK GDPR and the Data Protection Act 2018,
              42courses is the <strong>data controller</strong> of your personal
              data when you use our website and platform.
            </p>
            <h3>How to reach us</h3>
            <ul>
              <li>
                <strong>Email:</strong>{" "}
                <a href="mailto:groundcontrol@42courses.com">
                  groundcontrol@42courses.com
                </a>
              </li>
              <li>
                <strong>Registered office:</strong> D S Burge And Co Ltd, The
                Courtyard, 7 Francis Grove, London, SW19 4DW, United Kingdom
              </li>
              <li>
                <strong>Company number:</strong> 09650031 (registered in England
                and Wales)
              </li>
            </ul>
            <p>
              If you would prefer to write to our team about a specific privacy
              matter, mark the email <em>FAO: Data Protection</em> in the
              subject line. It will reach the right person faster than guessing
              job titles.
            </p>
            <p>
              <em>
                We are not required to appoint a Data Protection Officer under
                UK GDPR (we don't carry out large-scale monitoring or process
                special-category data at scale), and we haven't. Privacy
                questions are handled by our senior team via the email above.
              </em>
            </p>
          </div>
        </div>
      </section>
      <section
        id="what-we-collect"
        aria-labelledby="what-we-collect-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">02</span>
            <h2 className="section-title" id="what-we-collect-title">
              What we collect, <em>and why</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              We collect the bits we need to teach you, mark your work, take
              payment, and make the site less annoying. Not your shoe size. Not
              your blood type.{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              The table below shows every category of personal data we collect,
              what it is used for, and where it comes from. If a category isn't
              on this list, we don't have it.
            </p>
          </div>
          <div
            className="data-table"
            role="table"
            aria-label="What we collect, how, and why"
          >
            <div className="data-row" role="row">
              <div role="columnheader">Category</div>
              <div role="columnheader">What it includes</div>
              <div role="columnheader">Why we have it</div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Account</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Name, email, password (hashed), country, profile photo if you
                add one.
              </div>
              <div data-label="Why we have it" role="cell">
                So you can log in, we can email you, and your certificate has
                your name on it.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Learning context</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Your role, industry, location, goals, and what you tell us you'd
                like to achieve.
              </div>
              <div data-label="Why we have it" role="cell">
                So our AI can rewrite lessons around you. See{" "}
                <a href="#ai-privacy">AI & your data</a> below.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Work you submit</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Your answers to lesson questions, comments, posts in the
                Community Hub.
              </div>
              <div data-label="Why we have it" role="cell">
                So humans on our team can mark it, so you have a record, and so
                the community can see what you've shared.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Payment</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Billing name, billing address, last 4 digits of card, country
                for tax.{" "}
                <strong>Card numbers and CVVs never touch our servers.</strong>
              </div>
              <div data-label="Why we have it" role="cell">
                To take payment via Stripe and PayPal, and to issue invoices.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Technical</strong>
              </div>
              <div data-label="What it includes" role="cell">
                IP address, browser, device type, pages viewed, time on page,
                referrer.
              </div>
              <div data-label="Why we have it" role="cell">
                To keep the site secure, fix bugs, and make pages load faster.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Marketing</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Newsletter email, marketing preferences, open/click data if you
                opt in.
              </div>
              <div data-label="Why we have it" role="cell">
                So we can send the things you asked for, and stop sending the
                things you didn't.
              </div>
            </div>
            <div className="data-row" role="row">
              <div data-label="Category" role="cell">
                <strong>Support</strong>
              </div>
              <div data-label="What it includes" role="cell">
                Anything you write to <em>groundcontrol@</em>, plus our reply.
              </div>
              <div data-label="Why we have it" role="cell">
                So we can answer you, and so the next person on the team has
                context.
              </div>
            </div>
          </div>
          <div className="prose" style={{ marginTop: "18px" }}>
            <p>
              <em>
                We do not knowingly collect special category data (health,
                ethnicity, religion, political views, etc.) and would prefer you
                didn't share any. If you do, by accident or in a lesson answer,
                ask us to remove it and we will.
              </em>
            </p>
          </div>
        </div>
      </section>
      <section id="lawful-bases" aria-labelledby="lawful-bases-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">03</span>
            <h2 className="section-title" id="lawful-bases-title">
              The <em>lawful bases</em> we rely on.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              UK GDPR insists we name the legal reason we hold each bit of your
              data. There are six possible reasons. We use four of them.{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              For each category above, we rely on one of the following lawful
              bases under Article 6 of UK GDPR:
            </p>
            <ul>
              <li>
                <strong>Contract.</strong> When you buy a course or
                subscription, we process your data to deliver it. No contract,
                no course. (Account, learning context, work submitted, payment.)
              </li>
              <li>
                <strong>Legitimate interests.</strong> Some processing is just
                sensible business operation: keeping the site secure, preventing
                fraud, improving the product, asking past customers if they want
                to come back. (Technical data, fraud prevention, light
                analytics.) We have weighed this against your interests and
                concluded it's fair. You can object at any time. See{" "}
                <a href="#your-rights">Your rights</a>.
              </li>
              <li>
                <strong>Consent.</strong> Marketing emails, non-essential
                cookies, and anything else we ask permission for. You can
                withdraw consent at any time, just as easily as you gave it.
              </li>
              <li>
                <strong>Legal obligation.</strong> Tax records, accounting
                records, anything HMRC or another regulator can ask us to
                produce.
              </li>
            </ul>
          </div>
        </div>
      </section>
      <section
        id="ai-privacy"
        aria-labelledby="ai-privacy-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">04</span>
            <h2 className="section-title" id="ai-privacy-title">
              AI <em>and your data</em>.
            </h2>
          </div>
          <div className="ai-card">
            <div className="ai-card-head">
              <span className="ai-card-badge">The short answer</span>
            </div>
            <h3>
              Your data is not training fuel. Not ours, not anyone else's.
            </h3>
            <div className="prose" style={{ marginTop: "12px" }}>
              <p>
                When our AI rewrites a lesson around your role, location and
                goals, it does so to render the lesson <em>for you</em>. The
                information you give us:
              </p>
              <ul>
                <li>
                  is <strong>not used to train any AI model</strong>, by us or
                  by our suppliers;
                </li>
                <li>
                  is <strong>not retained</strong> by the AI provider beyond the
                  brief window needed to generate the lesson;
                </li>
                <li>
                  is <strong>not shared</strong> with any third party other than
                  the AI processor that produces the personalised text;
                </li>
                <li>
                  is <strong>not visible to other learners</strong>, ever.
                </li>
              </ul>
              <p>
                The same applies to anything you type into our AI tools inside
                the Community Hub: briefing tools, behavioural science
                applicators, and any tool we add later. Inputs stay private,
                outputs stay yours, nothing gets fed back into a training set.
              </p>
            </div>
          </div>
          <div className="prose">
            <h3>How this works under the hood</h3>
            <p>
              We use AI providers (the people who run the large models) as{" "}
              <strong>processors</strong>, which is the UK GDPR term for "a
              supplier we tell what to do". Our contracts with them require:
            </p>
            <ul>
              <li>
                <strong>No training</strong> on our customers' content, ever.
              </li>
              <li>
                <strong>Zero data retention</strong> beyond what's strictly
                required to generate a response.
              </li>
              <li>
                <strong>No sub-processing</strong> for purposes other than
                running our prompts.
              </li>
              <li>
                <strong>Notification</strong> of any data incident within the
                deadlines UK GDPR requires.
              </li>
            </ul>
            <h3>Marking is done by humans</h3>
            <p>
              Your answers to lesson questions are read and marked by a real
              human on our team, not an AI. We sometimes use AI to support our
              markers (catching typos, surfacing common patterns across many
              submissions), but the feedback you read is written by a person.
              Your answers are not used to train AI markers either, because we
              don't have AI markers.
            </p>
            <h3>Automated processing and your rights</h3>
            <p>
              Personalising a lesson is automated processing under UK GDPR. It
              is <strong>not</strong> automated decision-making in the Article
              22 sense: nothing 42courses does has legal or similarly
              significant effects on you. No grade, no certificate, no
              employment decision, no credit score, no access decision is made
              by a machine. Humans mark the work, humans award the certificates,
              and humans answer your support emails.
            </p>
            <p>
              Under UK GDPR you have the right to information about the logic of
              this processing and to object to it. The logic is simple: take the
              role, location and goals you optionally provide; rewrite the
              example layer of each lesson around them; keep all underlying
              facts and frameworks unchanged. You can object at any time by
              clearing those fields in your account settings.
            </p>
            <h3>What if you don't want AI personalisation?</h3>
            <p>
              You aren't forced into it. The role, location and goals fields are
              entirely optional. If you leave them blank, or close the tab, the
              course runs in its non-personalised form: the same lessons, the
              same frameworks, the same video content, just without examples
              tailored to your context. You can also clear or change those
              details at any time from your account settings.
            </p>
          </div>
        </div>
      </section>
      <section id="cookies" aria-labelledby="cookies-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">05</span>
            <h2 className="section-title" id="cookies-title">
              Cookies, <em>the digital kind</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              Small text files that remember things, like that you're logged in.
              Some are essential. Some you can turn off. None of them taste
              good.{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              We use four categories of cookie. The first kind we have to use;
              the rest you get to choose.
            </p>
            <h3>Strictly necessary</h3>
            <p>
              These keep you logged in, remember items in your basket, and
              protect against fraud. They cannot be switched off without
              breaking the site, so we don't ask permission for them. (UK PECR
              allows this.)
            </p>
            <h3>Functional</h3>
            <p>
              These remember your preferences (language, dark mode, last
              lesson). You can disable them in your browser without breaking
              anything important.
            </p>
            <h3>Analytics</h3>
            <p>
              We use Google Analytics (via Google Tag Manager) to see which
              pages people read and where they get stuck. Analytics data is
              anonymised and aggregated; we don't use it to identify you
              personally.
            </p>
            <h3>Marketing</h3>
            <p>
              If you've opted in, these help us measure whether our ads are
              working. If you haven't opted in, we don't drop them.
            </p>
            <p>
              You can change your cookie preferences at any time from the cookie
              banner, or via the link in our footer. You can also clear cookies
              in your browser settings. We'll respect your choice on every page
              after that, until you change it again.
            </p>
          </div>
        </div>
      </section>
      <section
        id="sharing"
        aria-labelledby="sharing-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">06</span>
            <h2 className="section-title" id="sharing-title">
              Who we <em>share data with</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              We share data only with the suppliers we need to run the platform.
              Each one is bound by a written contract that says, essentially,
              "do what 42courses tells you, nothing more".{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              We use the following categories of processor. Each one is
              contracted under UK GDPR-compliant terms, and each handles only
              the data they need for the job.
            </p>
            <ul>
              <li>
                <strong>Hosting and infrastructure:</strong> the cloud platform,
                database, and content delivery network that run the site and
                serve our lessons.
              </li>
              <li>
                <strong>Payments:</strong> Stripe and PayPal. Card numbers and
                CVVs are entered on their secure pages and never reach our
                servers. They are both certified to PCI DSS Level 1.
              </li>
              <li>
                <strong>Email:</strong> our transactional email provider (the
                system that sends you password resets, course confirmations, and
                lesson notifications).
              </li>
              <li>
                <strong>AI:</strong> the large-language-model providers that
                power lesson personalisation, under the strict terms set out in
                section 04.
              </li>
              <li>
                <strong>File uploads:</strong> the service that handles uploaded
                images and videos.
              </li>
              <li>
                <strong>Analytics:</strong> Google Analytics (via Google Tag
                Manager) for aggregated usage data.
              </li>
              <li>
                <strong>Customer support tools:</strong> the systems our small
                support team uses to read and reply to your emails.
              </li>
            </ul>
            <p>
              If you'd like the specific supplier names for any of the
              categories above, email us and we'll share them.
            </p>
            <p>
              We will share data with law enforcement only where we are legally
              required to, and we will tell you about it where we are legally
              allowed to.
            </p>
            <p>
              <strong>We do not sell personal data.</strong> We do not let
              advertisers buy access to our learner base. We do not share data
              with data brokers. We never have.
            </p>
          </div>
        </div>
      </section>
      <section
        id="retention"
        aria-labelledby="retention-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">07</span>
            <h2 className="section-title" id="retention-title">
              How long we <em>keep your data</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              Only as long as we have a good reason to keep it. We are a
              learning platform, not the British Museum.{" "}
            </div>
          </div>
          <div className="prose">
            <ul>
              <li>
                <strong>Account data:</strong> for as long as your account is
                active, plus 24 months after closure (so you can re-open it
                without losing your progress). "Active" means logging in at
                least once during that period. After that, deleted.
              </li>
              <li>
                <strong>Course work and certificates:</strong> kept while your
                account is active so you can re-download. Certificates are
                deleted with the account.
              </li>
              <li>
                <strong>Payment records:</strong> 7 years after the last
                transaction (HMRC requirement for limited companies).
              </li>
              <li>
                <strong>Support emails:</strong> retained in our email history
                so the next person on the team has context if you write in
                again. Ask us to delete a specific thread and we will, subject
                to any legal hold.
              </li>
              <li>
                <strong>Analytics:</strong> 14 months, aggregated and
                anonymised.
              </li>
              <li>
                <strong>Backups:</strong> rolling 30-day retention, then
                overwritten.
              </li>
            </ul>
            <p>
              If you ask us to delete your data sooner, we will, except for the
              bits the law requires us to keep (mainly the tax records).
            </p>
          </div>
        </div>
      </section>
      <section id="your-rights" aria-labelledby="your-rights-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">08</span>
            <h2 className="section-title" id="your-rights-title">
              Your rights, <em>all eight of them</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              UK GDPR gives you eight rights. We will not make you fight for any
              of them. Email us and we'll get on with it.{" "}
            </div>
          </div>
          <div className="rights-grid">
            <div className="right-card">
              <h4>1. Be informed</h4>
              <p>
                You have the right to know what we collect and why. You're
                reading it.
              </p>
            </div>
            <div className="right-card">
              <h4>2. Access</h4>
              <p>
                Ask for a copy of the personal data we hold about you. We'll
                send it within 30 days.
              </p>
            </div>
            <div className="right-card">
              <h4>3. Rectification</h4>
              <p>
                If something we hold is wrong, tell us, and we will correct it.
              </p>
            </div>
            <div className="right-card">
              <h4>4. Erasure</h4>
              <p>
                Ask us to delete your data ("right to be forgotten"). We will,
                except where the law requires us to keep it.
              </p>
            </div>
            <div className="right-card">
              <h4>5. Restrict processing</h4>
              <p>
                Ask us to pause processing while we sort out a dispute about
                accuracy.
              </p>
            </div>
            <div className="right-card">
              <h4>6. Data portability</h4>
              <p>
                Ask for your data in a structured, machine-readable format you
                can take elsewhere.
              </p>
            </div>
            <div className="right-card">
              <h4>7. Object</h4>
              <p>
                Object to processing based on legitimate interests, including
                for direct marketing.
              </p>
            </div>
            <div className="right-card">
              <h4>8. Withdraw consent</h4>
              <p>
                Where consent is the basis, you can withdraw it at any time,
                just as easily as you gave it.
              </p>
            </div>
          </div>
          <div className="prose" style={{ marginTop: "22px" }}>
            <p>
              To exercise any of these, email{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>
              . We will reply within 24 hours, often sooner, and complete most
              requests within 30 days. If we need longer for something genuinely
              complicated, we'll tell you why.
            </p>
            <p>
              <strong>If we mess up</strong>, you have the right to complain to
              the UK Information Commissioner's Office (the ICO). Their site is{" "}
              <a href="https://ico.org.uk" target="_blank" rel="noopener">
                ico.org.uk
              </a>{" "}
              and their helpline is 0303 123 1113. They are genuinely good at
              this. We'd still prefer you to come to us first so we can put it
              right, but skipping us is your call to make.
            </p>
          </div>
        </div>
      </section>
      <section
        id="children"
        aria-labelledby="children-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">09</span>
            <h2 className="section-title" id="children-title">
              Children, <em>and the under-16s</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              42courses is for grown-ups. If you are under 16, please ask a
              parent or guardian.{" "}
            </div>
          </div>
          <div className="prose">
            <p>
              42courses is designed for adults and is not intended for children
              under the age of 16. We do not knowingly collect personal data
              from anyone under 16. If you are a parent or guardian and believe
              your child has signed up, please email{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>{" "}
              and we will close the account and delete the data.
            </p>
            <p>
              Where a course is being used inside a school or other educational
              setting for learners aged 13 to 16, the school is responsible for
              obtaining the required parental consent, and we will work with
              them on a Data Processing Agreement under UK GDPR.
            </p>
          </div>
        </div>
      </section>
      <section id="security" aria-labelledby="security-title">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">10</span>
            <h2 className="section-title" id="security-title">
              How we <em>keep your data safe</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text">
              {" "}
              Encryption in transit, encryption at rest, tight access controls,
              and the boring discipline that keeps everything boringly
              uneventful.{" "}
            </div>
          </div>
          <div className="prose">
            <ul>
              <li>
                <strong>In transit:</strong> Everything between your browser and
                our servers runs over HTTPS / TLS 1.2 or higher.
              </li>
              <li>
                <strong>At rest:</strong> Database storage is encrypted at the
                disk level. Passwords are hashed with industry-standard one-way
                hashing (we cannot see your password; nor can anyone we work
                with).
              </li>
              <li>
                <strong>Access:</strong> Only the small number of 42courses
                staff who need access to live data have it. Every action they
                take is logged.
              </li>
              <li>
                <strong>Payments:</strong> Card details are handled by Stripe
                and PayPal under PCI DSS Level 1. They never touch our servers.
              </li>
              <li>
                <strong>Backups:</strong> Encrypted, rotated, and held under the
                same controls as live data.
              </li>
              <li>
                <strong>Incident response:</strong> If something goes wrong, we
                follow a written incident plan. If there is a risk to your
                rights, we will tell you within 72 hours, in line with UK GDPR.
              </li>
            </ul>
            <p>
              No system is invulnerable, ours included. If you spot a security
              problem, please tell us at{" "}
              <a href="mailto:security@42courses.com">security@42courses.com</a>
              . We won't punish you for the message.
            </p>
          </div>
        </div>
      </section>
      <section
        id="changes"
        aria-labelledby="changes-title"
        style={{
          background: "var(--cream-ink)",
          borderTop: "1px solid var(--grey-200)",
          borderBottom: "1px solid var(--grey-200)",
        }}
      >
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">11</span>
            <h2 className="section-title" id="changes-title">
              Changes <em>to this policy</em>.
            </h2>
          </div>
          <div className="plain">
            <span className="plain-tag">In plain English</span>
            <div className="plain-text"> We'll tell you. We won't sneak. </div>
          </div>
          <div className="prose">
            <p>
              We may update this policy from time to time, usually because we've
              added a new feature, changed a supplier, or improved a security
              control. When we do, we'll change the version on this page. For
              material changes, we will email you and give you at least 30 days'
              notice before the change takes effect.
            </p>
            <p>
              If you don't agree with a change, you can close your account
              before it takes effect, and (where applicable) request a pro-rated
              refund. The 30-day refund clause in our{" "}
              <a href="/conditions">Terms</a> still applies on top of this.
            </p>
          </div>
        </div>
      </section>
      <section id="contact" aria-labelledby="contact-title-section">
        <div className="narrow">
          <div className="section-head">
            <span className="section-num">12</span>
            <h2 className="section-title" id="contact-title-section">
              Contacting <em>us</em>.
            </h2>
          </div>
          <div className="contact-box">
            <h3>
              One inbox, <em>one team, one human reply</em>.
            </h3>
            <p>
              Privacy questions, data requests, security disclosures, anything
              covered (or not covered) by this policy: send it to{" "}
              <a href="mailto:groundcontrol@42courses.com">
                groundcontrol@42courses.com
              </a>{" "}
              and we'll route it to the right person internally.
            </p>
            <p>
              <strong>Mark the subject "FAO: Data Protection"</strong> if your
              message is specifically about your personal data. We reply within
              24 hours, often sooner.
            </p>
            <p>
              For complaints, you can also write to the UK Information
              Commissioner's Office at{" "}
              <a
                href="https://ico.org.uk/make-a-complaint"
                target="_blank"
                rel="noopener"
              >
                ico.org.uk/make-a-complaint
              </a>
              . They take complaints in plain English too.
            </p>
          </div>
        </div>
      </section>
    </main>
    <section className="still-got-q" aria-labelledby="still-q-title">
      <div className="container">
        <h2 id="still-q-title">
          Still got a <em>question</em>?
        </h2>
        <p>
          If the answer isn't on this page, email a real human at 42courses. We
          reply within 24 hours, often sooner.
        </p>
        <div className="btn-row">
          <a
            href="mailto:groundcontrol@42courses.com"
            className="btn btn-primary"
          >
            Email Ground Control
          </a>
          <a href="/faqs" className="btn btn-secondary">
            Read the FAQs
          </a>
        </div>
      </div>
    </section>
    <footer className="footer-egg">
      <div className="big">
        <em>Don't panic.</em>
      </div>
      <div className="small">
        Your data is safe. Your towel is your own affair.
      </div>
      <div className="version">Version: 12 May 2026</div>
    </footer>
  </LegalPageShell>
);

export default Privacy;
